"use client"

import { useState } from "react"
import {
  ImportOptions as ImportOptionsType,
  ImportJobResult,
} from "../../../types"
import { processBatchedPosts } from "../../../utils/importHelpers"
import { ImportStatus } from "./types"

/**
 * Hook for managing the actual import process
 */
export default function useRSSImport(
  posts: any[] | null,
  importOptions: ImportOptionsType
) {
  const [importStatus, setImportStatus] = useState<ImportStatus>("UNSTARTED")
  const [isImportLoading, setIsImportLoading] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  // Import progress tracking
  const [numFinished, setNumFinished] = useState(0)
  const [skippedPosts, setSkippedPosts] = useState<
    { title: string; reason?: string }[]
  >([])
  const [erroredPosts, setErroredPosts] = useState<
    { title: string; reason?: string }[]
  >([])
  const [importJobDetails, setImportJobDetails] =
    useState<ImportJobResult | null>(null)

  /**
   * Handles the batch import process for all posts
   */
  const handleImportPosts = async () => {
    if (!posts) return

    setIsImportLoading(true)
    setImportStatus("IN_PROGRESS")
    setImportError(null)
    setNumFinished(0)
    setSkippedPosts([])
    setErroredPosts([])

    try {
      // Process posts in batches and track progress
      const { successful, skipped, errors } = await processBatchedPosts(
        posts,
        importOptions,
        5, // Batch size
        (results) => {
          // Update progress as batches complete
          setNumFinished(results.successful.length)
          setSkippedPosts(results.skipped)
          setErroredPosts(results.errors)
        }
      )

      // Create snapshot of current state to ensure consistency in final results
      const totalResults: ImportJobResult = {
        total: posts.length,
        successful: successful.length,
        skipped: skipped.length,
        errors: errors.length,
        skippedDetails: skipped,
        errorDetails: errors,
      }

      // Update job details for the completion modal
      setImportJobDetails(totalResults)
      setImportStatus("FINISHED")
    } catch (error) {
      console.error("Error importing posts:", error)
      setImportError("Failed to import posts. Please try again.")
    } finally {
      setIsImportLoading(false)
    }
  }

  // Calculate remaining posts to show in progress
  const postsRemaining =
    (posts?.length || 0) -
    numFinished -
    skippedPosts.length -
    erroredPosts.length

  return {
    importStatus,
    isImportLoading,
    importError,
    numFinished,
    skippedPosts,
    erroredPosts,
    importJobDetails,
    postsRemaining,
    handleImportPosts,
  }
}
